import { FileExtEnum, OcrResult } from "@finway-group/shared/lib/models"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { XmlPlaceholderImage } from "../../shared/config/consts"
import { isFileExtensionMatching } from "../../shared/utils/file.utils"
import { EInvoiceXmlRenderer } from "../einvoice"
import { drawStampOnCanvas } from "../fileViewer/fileViewerCanvas.utils"

interface DocumentInformationProps {
    fileName: string
    isEInvoice?: boolean
    isShowingPaidStamp?: boolean
    ocrResults?: OcrResult
}

export const getInvoiceXMLRenderer = ({ isShowingPaidStamp = false, isEInvoice, fileName, ocrResults }: DocumentInformationProps) => {
    if (!isFileExtensionMatching(fileName, FileExtEnum.XML)) {
        return undefined
    }

    if (isEInvoice && ocrResults?.eInvoiceData) {
        return () => <EInvoiceXmlRenderer data={ocrResults} />
    }

    return () => <XmlInvoicePlaceholder isShowingPaidStamp={isShowingPaidStamp} isEInvoice={isEInvoice} />
}

const XmlInvoicePlaceholder = ({ isEInvoice, isShowingPaidStamp }: Omit<DocumentInformationProps, "fileName">) => {
    const image = isEInvoice ? XmlPlaceholderImage.XRECHNUNG : XmlPlaceholderImage.XML
    const alt = isEInvoice ? "xrechnung" : "xml"

    const { t } = useTranslation()

    const canvasRef = useRef<HTMLCanvasElement>(null)
    const imgRef = useRef<HTMLImageElement>(null)

    const clearCanvas = (canvas: HTMLCanvasElement) => {
        const ctx = canvas.getContext("2d") as CanvasRenderingContext2D
        ctx.clearRect(0, 0, canvas.width, canvas.height)
    }

    const drawStamp = () => {
        const canvas = canvasRef.current
        const img = imgRef.current

        if (!canvas || !img) {
            return
        }

        canvas.width = img.clientWidth
        canvas.height = img.clientHeight

        clearCanvas(canvas)
        if (isShowingPaidStamp) {
            drawStampOnCanvas({ canvas, stamp: t("label:paid") })
        }
    }

    useEffect(() => {
        drawStamp()
    }, [isShowingPaidStamp])

    return (
        <div className="h-full w-full">
            <canvas ref={canvasRef} className="absolute m-auto" style={{ top: "15%", bottom: 0, left: 0, right: 0 }} />
            <img src={image} ref={imgRef} alt={alt} onLoad={drawStamp} className="object-contain m-auto h-full" />
        </div>
    )
}
